import React from "react";
import { ArrowRightLeft, ShoppingBag, ShieldCheck, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/ui/container";
import Link from "next/link";
import { routes } from "@/routes";

const IconCheck = () => (
  <svg
    className="h-5 w-5 text-green-500 mr-2 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="3"
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const SkeletonListingPreview = () => (
  <div className="bg-card border rounded-lg shadow-sm overflow-hidden max-w-xs mx-auto">
    <div className="h-32 bg-muted flex items-center justify-center">
      <ShoppingBag className="h-10 w-10 text-muted-foreground/50" />
    </div>
    <div className="p-4 space-y-2">
      <div className="h-4 w-3/4 bg-muted rounded animate-pulse" />
      <div className="h-3 w-1/2 bg-muted rounded animate-pulse" />
      <div className="flex justify-between items-center mt-3 pt-2 border-t border-border/50">
        <div className="h-4 w-1/3 bg-muted rounded animate-pulse" />
        <div className="h-6 w-16 bg-primary/20 rounded-full animate-pulse" />
      </div>
    </div>
  </div>
);

const SkeletonTradePreview = () => (
  <div className="bg-card border rounded-lg shadow-sm p-4 max-w-xs mx-auto">
    <div className="flex items-center justify-between mb-4">
      <div className="flex flex-col items-center">
        <div className="w-10 h-10 rounded-full bg-muted mb-1" />
        <div className="h-2 w-12 bg-muted rounded" />
      </div>
      <ArrowRightLeft className="h-5 w-5 text-primary" />
      <div className="flex flex-col items-center">
        <div className="w-10 h-10 rounded-full bg-muted mb-1" />
        <div className="h-2 w-12 bg-muted rounded" />
      </div>
    </div>
    <div className="space-y-2 text-center">
      <div className="text-xs text-muted-foreground">Proposed Trade</div>
      <div className="h-4 w-full bg-muted rounded animate-pulse" />
      <div className="h-3 w-2/3 mx-auto bg-muted rounded animate-pulse" />
    </div>
  </div>
);

export const ClubSwapSection = () => {
  return (
    <section
      id="club-swap-section"
      className="w-full py-12 md:py-24 lg:py-32 relative overflow-hidden"
      style={{
        backgroundImage: "url(/bgs/bg6.webp)",
        backgroundSize: "cover",
        backgroundPosition: "center 30%",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Gradient overlay for readability with subtle theme preservation */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/85 to-background/95" />
      <Container className="relative z-10">
        <div className="text-center mb-12 md:mb-16 px-4 md:px-0">
          <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-primary text-primary-foreground hover:bg-primary/80 mb-4">
            New Feature
          </div>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4">
            Club Swap: The Marketplace for Golfers
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto px-2 sm:px-0">
            Upgrade your bag without breaking the bank. Buy, sell, and trade
            clubs directly with trusted golfers in your local community.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {/* Feature 1: Buy & Sell */}
          <div className="flex flex-col p-6 rounded-lg bg-background shadow-lg hover:shadow-xl transition-all border border-border/50">
            <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4 text-blue-600">
              <ShoppingBag className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Buy & Sell Locally</h3>
            <p className="text-muted-foreground mb-4 flex-grow">
              Find great deals on pre-owned clubs near you. List your old gear
              in minutes and turn it into cash for your next upgrade.
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground mb-6">
              <li className="flex items-start">
                <IconCheck />
                <span>Easy listing process with photo uploads</span>
              </li>
              <li className="flex items-start">
                <IconCheck />
                <span>Filter by brand, type, and condition</span>
              </li>
            </ul>
            <div className="mt-auto">
              <SkeletonListingPreview />
            </div>
          </div>

          {/* Feature 2: Trade */}
          <div className="flex flex-col p-6 rounded-lg bg-background shadow-lg hover:shadow-xl transition-all border border-border/50">
            <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center mb-4 text-green-600">
              <ArrowRightLeft className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Trade Your Gear</h3>
            <p className="text-muted-foreground mb-4 flex-grow">
              Have a driver that doesn't fit your swing? Swap it for something
              that does. Propose direct trades with other members.
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground mb-6">
              <li className="flex items-start">
                <IconCheck />
                <span>Direct trade proposals</span>
              </li>
              <li className="flex items-start">
                <IconCheck />
                <span>Negotiate terms securely</span>
              </li>
            </ul>
            <div className="mt-auto">
              <SkeletonTradePreview />
            </div>
          </div>

          {/* Feature 3: Trusted Community */}
          <div className="flex flex-col p-6 rounded-lg bg-background shadow-lg hover:shadow-xl transition-all border border-border/50">
            <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center mb-4 text-purple-600">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Trusted Community</h3>
            <p className="text-muted-foreground mb-4 flex-grow">
              Deal with confidence. Our community-focused platform connects you
              with verified local golfers and league members.
            </p>
            <ul className="space-y-2 text-sm text-muted-foreground mb-6">
              <li className="flex items-start">
                <IconCheck />
                <span>View seller profiles and history</span>
              </li>
              <li className="flex items-start">
                <IconCheck />
                <span>Safe, local meetups at courses</span>
              </li>
            </ul>
            <div className="mt-auto flex items-center justify-center h-32 bg-muted/30 rounded-lg border border-dashed border-muted-foreground/30">
              <div className="text-center p-4">
                <p className="text-sm font-medium text-muted-foreground">
                  Verified Members
                </p>
                <div className="flex -space-x-2 justify-center mt-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full bg-muted border-2 border-background"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <Link href={routes.register}>
            <Button size="lg" className="px-8">
              Start Trading Today
            </Button>
          </Link>
        </div>
      </Container>
    </section>
  );
};
